import { TIPS_DEFAULT_VALUES } from 'src/app/core/constants/cashregister-settings';

import type { SettingsContext, SettingsSource } from '../models/settings-hub.model';
import { legacyPercentageFilter, parseIndexedTexts, resizeIndexedTexts } from '../utils/settings-hub.utils';

import { field } from './settings-builders';
import {
    bookingWarningOptions,
    defaultExitOptions,
    departmentOptions,
    expenseReportTemplateOptions,
    publicReceiptDurationOptions,
    queueModeOptions,
    queuePrintTypeOptions,
    rangeOptions
} from './settings-options';
import { DIGITS_PATTERN } from './settings-patterns';
import { inCountry, notInCountry } from './settings-rules';

const coversEnabled = ({ values }: SettingsContext) => values['orders.automated_add_cover'] === true;
const tipsEnabled = ({ values }: SettingsContext) => values['cashregister.tip.enable'] === true;
const queueEnabled = ({ values }: SettingsContext) => !!values['cashregister.queue.mode'];

export const CASHREGISTER_SUBCATEGORY: SettingsSource = {
    id: 'cashregister-orders',
    cards: [
        {
            id: 'products',
            settings: [
                field.toggle('showcase_hide_empty_categories', 'SETTINGS.SETTINGS.HIDE_EMPTY_CATEGORIES'),
                field.toggle('orders.add_item_as_new', 'SETTINGS.SETTINGS.PRODUCT_ON_NEW_LINE'),
                field.toggle('orders.half_portion_enable', 'SETTINGS.SETTINGS.ALLOW_HALF_PORTION'),
                // A free text, always shown, as in the legacy page
                field.text('orders.half_portion_discount_value', 'SETTINGS.SETTINGS.HALF_PORTION_DISCOUNT_VALUE', { suffix: '%' }),
                field.toggle('orders.ingredients_removal_disabled', 'SETTINGS.SETTINGS.INGREDIENTS_REMOVAL_DISABLED'),
                field.toggle('cashregister.add_item_on_barcode_not_found', 'SETTINGS.SETTINGS.ADD_ITEM_ON_BARCODE_NOT_FOUND'),
                field.toggle('cashregister.error_sound_on_barcode_not_found', 'SETTINGS.SETTINGS.ERROR_SOUND_ON_BARCODE_NOT_FOUND'),
                {
                    id: 'cashregister.split_sale.split_items_on_dutch_treat',
                    preferenceKey: 'cashregister.split_sale.split_items_on_dutch_treat',
                    control: 'toggle',
                    label: 'SETTINGS.SETTINGS.SPLIT_ITEMS_ON_DUTCH_TREAT',
                    disabledWhen: ({ getPreference }) => !!getPreference('pienissimo.token')
                }
            ]
        },
        {
            id: 'covers',
            settings: [
                field.toggle('orders.automated_add_cover', 'SETTINGS.SETTINGS.ADD_COVER_DURING_CHECKOUT'),
                {
                    id: 'orders.automated_add_cover.type',
                    preferenceKey: 'orders.automated_add_cover.type',
                    control: 'select',
                    label: 'SETTINGS.SETTINGS.COVER_TYPE',
                    options: () => [
                        { value: 'id', label: 'SETTINGS.SETTINGS.PRODUCT', translate: true },
                        { value: 'perc', label: 'SETTINGS.SETTINGS.SURCHARGE', translate: true }
                    ],
                    resetOnChange: ['orders.automated_add_cover.value'],
                    visibleWhen: coversEnabled
                },
                {
                    id: 'orders.automated_add_cover.item',
                    preferenceKey: 'orders.automated_add_cover.value',
                    control: 'item-picker',
                    defaultValue: 0,
                    label: 'SETTINGS.SETTINGS.SELECTED_COVER_ITEM',
                    visibleWhen: context => coversEnabled(context) && context.values['orders.automated_add_cover.type'] === 'id'
                },
                {
                    id: 'orders.automated_add_cover.value',
                    preferenceKey: 'orders.automated_add_cover.value',
                    control: 'number',
                    defaultValue: 0,
                    label: 'SETTINGS.SETTINGS.COVER_SURCHARGE_PERCENTAGE',
                    min: 0,
                    suffix: '%',
                    visibleWhen: context => coversEnabled(context) && context.values['orders.automated_add_cover.type'] === 'perc'
                },
                {
                    id: 'cashregister.prebill.covers_info.enabled',
                    preferenceKey: 'cashregister.prebill.covers_info.enabled',
                    control: 'toggle',
                    label: 'SETTINGS.SETTINGS.PREBILL_COVERS_INFO_ENABLED',
                    visibleWhen: coversEnabled
                },
                {
                    id: 'cashregister.prebill.covers_info.per_cover_amount_label',
                    preferenceKey: 'cashregister.prebill.covers_info.per_cover_amount_label',
                    control: 'text',
                    label: 'SETTINGS.SETTINGS.PREBILL_COVERS_INFO_PER_COVER_AMOUNT_LABEL',
                    visibleWhen: context => coversEnabled(context) && context.values['cashregister.prebill.covers_info.enabled'] === true
                },
                field.toggle('orders.apply_cover_to_total', 'SETTINGS.SETTINGS.APPLY_COVER_TO_TOTAL')
            ]
        },
        {
            id: 'orders',
            settings: [
                field.toggle('orders.skip_creation_dialog', 'SETTINGS.SETTINGS.SKIP_ORDER_CREATION'),
                field.toggle('orders.ingredients_removal_affects_price', 'SETTINGS.SETTINGS.INGREDIENTS_REMOVAL_AFFECTS_PRICE'),
                field.toggle('orders.drop_variations_on_park', 'SETTINGS.SETTINGS.DISCARD_VARIATIONS_AT_PARKING')
            ]
        },
        {
            id: 'orders-flow',
            settings: [
                field.toggle('orders.open_tables_after_park', 'SETTINGS.SETTINGS.GO_TO_TABLES_AFTER_PARKING'),
                field.toggle('orders.open_tables_after_send', 'SETTINGS.SETTINGS.GO_TO_TABLES_AFTER_SEND_ORDER'),
                field.toggle('orders.open_tables_after_skip_send', 'SETTINGS.SETTINGS.GO_TO_TABLES_AFTER_SKIP_SEND_ORDER'),
                field.toggle('cashregister.open_tables_after_send', 'SETTINGS.SETTINGS.GO_TO_TABLES_AFTER_PRINT'),
                field.toggle('cashregister.keep_table_open', 'SETTINGS.SETTINGS.CASHREGISTER_KEEP_TABLE_OPEN')
            ]
        },
        {
            id: 'orders-management',
            settings: [
                field.toggle('orders.require_table_selection', 'SETTINGS.SETTINGS.REQUIRE_TABLE_SELECTION'),
                field.text('tables.hanging_alarm_after', 'SETTINGS.SETTINGS.TABLES_HANGING_ALARM'),
                field.toggle('orders.skip_checkout_confirm', 'SETTINGS.SETTINGS.NO_ASK_REQUEST_TOTAL'),
                field.toggle('cashregister.disable_menu_ingredients_pricing', 'SETTINGS.SETTINGS.DISABLE_MENU_INGREDIENTS_PRICING')
            ]
        },
        {
            id: 'orders-printing',
            settings: [
                field.toggle('cashregister.print_orders_on_sale_close', 'SETTINGS.SETTINGS.PRINT_ORDERS_ON_SALE_CLOSE'),
                field.toggle('cashregister.enable_print_order', 'SETTINGS.SETTINGS.ENABLE_PRINT_ORDER'),
                field.toggle('orders.print_replacement_order_for_delivery_takeaway', 'SETTINGS.SETTINGS.PRINT_REPLACEMENT_ORDER_FOR_DELIVERY_TAKEAWAY'),
                field.toggle('cashregister.print_order_header_variations', 'SETTINGS.SETTINGS.PRINT_ORDER_HEADER_VARIATIONS'),
                {
                    id: 'orders.exits',
                    preferenceKey: 'orders.exits',
                    control: 'select',
                    numeric: true,
                    label: 'SETTINGS.SETTINGS.ORDERS_EXITS',
                    options: () => rangeOptions(0, 100),
                    // The names of the exits follow their number; the ones removed are remembered by the saved preference.
                    // A default exit that no longer exists would be saved without showing in the select: it goes back to "no exit"
                    onChange: (exits, { values, getSavedPreference }) => ({
                        'orders.exits_names': resizeIndexedTexts(values['orders.exits_names'], parseIndexedTexts(getSavedPreference('orders.exits_names')), Number(exits) || 0),
                        ...(Number(values['orders.default_exit']) > (Number(exits) || 0) ? { 'orders.default_exit': 0 } : {})
                    })
                },
                {
                    id: 'orders.exits_names',
                    preferenceKey: 'orders.exits_names',
                    control: 'indexed-texts',
                    label: 'SETTINGS.SETTINGS.ORDERS_EXITS',
                    indexedTexts: { countKey: 'orders.exits', itemLabel: 'SETTINGS.SETTINGS.ORDERS_EXIT_NAME' }
                },
                {
                    id: 'orders.default_exit',
                    preferenceKey: 'orders.default_exit',
                    control: 'select',
                    numeric: true,
                    label: 'SETTINGS.SETTINGS.DEFAULT_EXIT',
                    options: defaultExitOptions,
                    optionsDependOn: ['orders.exits']
                },
                field.select('orders.print_attempts', 'SETTINGS.SETTINGS.ORDERS_PRINT_ATTEMPTS', { numeric: true, options: () => rangeOptions(1, 10) }),
                field.toggle('cashregister.print_nonfiscal_sale_on_store', 'SETTINGS.SETTINGS.PRINT_NONFISCAL_SALE_ON_STORE', { visibleWhen: inCountry('IT') })
            ]
        },
        {
            id: 'sales-process',
            settings: [
                field.toggle('general.simplified_accessibility', 'SETTINGS.SETTINGS.GENERAL_SIMPLIFIED_ACCESSIBILITY'),
                field.toggle('cashregister.hide_zero_price_warnings', 'SETTINGS.SETTINGS.SKIP_ZERO_PRICE_CHECK'),
                field.toggle('cashregister.skip_archive_confirmation', 'SETTINGS.SETTINGS.SKIP_ARCHIVE_CONFIRMATION', { visibleWhen: inCountry('IT') }),
                field.toggle('cashregister.open_cashdrawer_on_store', 'SETTINGS.SETTINGS.OPEN_CASHDRAWER_ON_STORE'),
                field.toggle('cashregister.ask_seller_for_each_sale', 'SETTINGS.SETTINGS.ASK_SELLER_FOR_EACH_SALE'),
                field.toggle('cashregister.register_cashdrawers_paid_amount', 'SETTINGS.SETTINGS.REGISTER_CASHDRAWERS_PAID_AMOUNT'),
                field.toggle('cashregister.disable_cash_limit_exceeded_confirm', 'SETTINGS.SETTINGS.DISABLE_CASH_LIMIT_EXCEEDED_CONFIRM'),
                field.toggle('printers.verify_cash_on_dailyclousure', 'SETTINGS.SETTINGS.VERIFY_CASH_ON_DAILYCLOUSURE'),
                field.toggle('cashregister.disable_customer_invoice_prompt', 'SETTINGS.SETTINGS.DISABLE_CUSTOMER_INVOICE_PROMPT'),
                field.toggle('cashregister.enable_payment_rounding', 'SETTINGS.SETTINGS.ENABLE_PAYMENT_ROUNDING'),
                field.toggle('cashregister.lock_sale_after_prebill', 'SETTINGS.SETTINGS.CASHREGISTER_LOCK_SALE_AFTER_PREBILL'),
                field.toggle('cashregister.require_buzzer_input', 'SETTINGS.SETTINGS.REQUIRE_BUZZER_INPUT'),
                field.toggle('cashregister.payments.force_prepaid_credit', 'SETTINGS.SETTINGS.FORCE_PREPAID_CREDIT'),
                field.toggle('cashregister.enable_split_sale', 'SETTINGS.SETTINGS.ENABLE_SPLIT_SALE'),
                {
                    id: 'cashregister.split_sale.default_department_id',
                    preferenceKey: 'cashregister.split_sale.default_department_id',
                    control: 'select',
                    numeric: true,
                    defaultValue: 0,
                    label: 'SETTINGS.SETTINGS.SPLIT_SALE_DEFAULT_DEPARTMENT',
                    options: context => [{ value: 0, label: 'CASHREGISTER.SPLIT_SALE.SPLIT_BY_VAT', translate: true }, ...departmentOptions(context)]
                },
                field.toggle('cashregister.disable_split_sale_amount_covers', 'SETTINGS.SETTINGS.DISABLE_SPLIT_SALE_BY_AMOUNT_COVERS'),
                field.toggle('cashregister.daily_report_per_seller', 'SETTINGS.SETTINGS.DAILY_REPORT_PER_SELLER', { visibleWhen: notInCountry('IT') }),
                field.toggle('cashregister.table_name_on_topbar', 'SETTINGS.SETTINGS.TABLE_NAME_ON_TOPBAR'),
                field.toggle('cashregister.deselect_sales_from_menu_bar', 'SETTINGS.SETTINGS.DESELECT_SALES_FROM_MENU_BAR'),
                field.toggle('cashregister.request_prebill_from_stored', 'SETTINGS.SETTINGS.REQUEST_PREBILL_FROM_STORED'),
                field.toggle('cashregister.return_to_history_after_summary_emission', 'SETTINGS.SETTINGS.RETURN_TO_HISTORY_AFTER_SUMMARY_EMISSION'),
                field.toggle('history.show_stored_sales', 'SETTINGS.SETTINGS.HISTORY_SHOW_STORED_SALES'),
                field.toggle('verifone.enable_extended_receipt', 'SETTINGS.SETTINGS.VERIFONE_ENABLE_EXTENDED_RECEIPT', { visibleWhen: inCountry('GB') })
            ]
        },
        {
            id: 'sales-items',
            settings: [
                field.toggle('cashregister.add_item_as_new', 'SETTINGS.SETTINGS.PRODUCT_ON_NEW_LINE'),
                field.toggle('cashregister.check_stock', 'SETTINGS.SETTINGS.CASHREGISTER_CHECK_STOCK'),
                field.toggle('cashregister.count_nostock_as_unavailable', 'SETTINGS.SETTINGS.CASHREGISTER_COUNT_NOSTOCK_AS_UNAVAILABLE'),
                field.toggle('cashregister.disable_required_variations', 'SETTINGS.SETTINGS.DISABLE_REQUIRED_VARIATIONS'),
                field.toggle('cashregister.disable_manual_production_confirm', 'SETTINGS.SETTINGS.CASHREGISTER_DISABLE_MANUAL_PRODUCTION_CONFIRM'),
                field.toggle('cashregister.remove_zero_price_items_from_receipt', 'SETTINGS.SETTINGS.REMOVE_ZERO_PRICE_ITEMS_FROM_RECEIPT'),
                field.toggle('cashregister.enable_switch_price_list', 'SETTINGS.SETTINGS.CASHREGISTER_ENABLE_SWITCH_PRICE_LIST'),
                field.toggle('cashregister.reset_pricelist_on_close', 'SETTINGS.SETTINGS.CASHREGISTER_RESET_PRICELIST_ON_CLOSE'),
                field.toggle('cashregister.enable_research_product_by_3_chars', 'SETTINGS.SETTINGS.ENABLE_RESEARCH_PRODUCT_BY_3_CHARS')
            ]
        },
        {
            id: 'sales-barcode',
            settings: [
                field.toggle('cashregister.dynamic_scales_barcode', 'SETTINGS.SETTINGS.DYNAMIC_SCALES_BARCODE'),
                field.toggle('cashregister.on_taxcode_not_found_show_add_customer', 'SETTINGS.SETTINGS.ON_TAXCODE_NOT_FOUND_SHOW_ADD_CUSTOMER')
            ]
        },
        {
            id: 'sales-keypad',
            settings: [
                field.toggle('cashregister.keyboard.integer_multiply', 'SETTINGS.SETTINGS.CASHREGISTER_KEYBOARD_INTEGER_MULTIPLY'),
                field.toggle('cashregister.keyboard.set_quantity', 'SETTINGS.SETTINGS.CASHREGISTER_KEYBOARD_SET_QUANTITY'),
                field.toggle('cashregister.keyboard.quantity_is_integer', 'SETTINGS.SETTINGS.CASHREGISTER_KEYBOARD_QUANTITY_IS_INTEGER'),
                field.text('cashregister.dynamic_item_price_check', 'SETTINGS.SETTINGS.DYNAMIC_ITEM_PRICE_CHECK', { pattern: DIGITS_PATTERN })
            ]
        },
        {
            id: 'sales-credits',
            settings: [
                {
                    id: 'prepaid.recharge_department_id',
                    preferenceKey: 'prepaid.recharge_department_id',
                    control: 'select',
                    numeric: true,
                    label: 'SETTINGS.SETTINGS.RECHARGE_DEPARTMENT',
                    options: departmentOptions,
                    visibleWhen: ({ isModuleEnabled }) => isModuleEnabled('prepaid')
                },
                field.toggle('cashregister.send_giftcards_as_unclaimed_payments', 'SETTINGS.SETTINGS.SEND_GIFTCARDS_AS_UNCLAIMED_PAYMENTS'),
                field.toggle('cashregister.tip.enable', 'SETTINGS.SETTINGS.TIP_ENABLED'),
                {
                    id: 'cashregister.tip.predefined_values',
                    preferenceKey: 'cashregister.tip.predefined_values',
                    control: 'chips',
                    label: 'SETTINGS.SETTINGS.TIP_PERCENTAGES',
                    chipFilter: legacyPercentageFilter,
                    maxItems: 3,
                    defaultValue: TIPS_DEFAULT_VALUES,
                    visibleWhen: tipsEnabled
                },
                {
                    id: 'cashregister.tip.department',
                    preferenceKey: 'cashregister.tip.department',
                    control: 'select',
                    numeric: true,
                    label: 'SETTINGS.SETTINGS.TIP_DEPARTMENT',
                    options: departmentOptions,
                    visibleWhen: tipsEnabled
                },
                field.text('cashregister.tip.reference_text', 'SETTINGS.SETTINGS.TIP_NORMATIVE_REFERENCE', { visibleWhen: tipsEnabled }),
                {
                    id: 'cashregister.quick_coupons_list',
                    preferenceKey: 'cashregister.quick_coupons_list',
                    control: 'object-list',
                    label: 'SETTINGS.SETTINGS.QUICK_COUPONS_LIST',
                    objectList: {
                        addLabel: 'SETTINGS.SETTINGS.QUICK_COUPONS_ADD',
                        fields: [
                            { key: 'name', label: 'SETTINGS.HUB.FIELDS.NAME', control: 'text', required: true, grow: 2 },
                            { key: 'value', label: 'SETTINGS.HUB.FIELDS.VALUE', control: 'number', required: true },
                            { key: 'department_id', label: 'SETTINGS.HUB.FIELDS.DEPARTMENT', control: 'select', required: true, grow: 2, options: departmentOptions }
                        ]
                    }
                },
                field.select('cashregister.expense_report.template_id', 'SETTINGS.SETTINGS.EXPENSE_REPORT_TEMPLATE', { numeric: true, options: expenseReportTemplateOptions }),
                field.select('cashregister.expense_report.department_id', 'SETTINGS.SETTINGS.EXPENSE_REPORT_DEPARTMENT', { numeric: true, options: departmentOptions })
            ]
        },
        {
            id: 'sales-documents',
            settings: [
                field.select('cashregister.queue.mode', 'SETTINGS.SETTINGS.QUEUE_MODE', { options: queueModeOptions }),
                field.multiselect('cashregister.queue.print_type', 'SETTINGS.SETTINGS.QUEUE_PRINT_TYPE', { options: queuePrintTypeOptions, visibleWhen: queueEnabled }),
                field.number('cashregister.queue.max_value', 'SETTINGS.SETTINGS.QUEUE_MAX_VALUE', { min: 0, max: 100, defaultValue: 99, visibleWhen: queueEnabled }),
                field.select('fiscalprinter.e_invoice_receipt_copies', 'SETTINGS.SETTINGS.FISCALPRINTER_E_INVOICE_RECEIPT_COPIES', { numeric: true, options: () => rangeOptions(1, 5) }),
                field.toggle('nofiscalprinter.disable_nonfiscal_sale_header', 'SETTINGS.SETTINGS.DISABLE_NONFISCAL_SALE_HEADER'),
                field.toggle('cashregister.save_paper_on_prebill', 'SETTINGS.SETTINGS.SAVE_PAPER_ON_PREBILL'),
                field.toggle('cashregister.save_paper_on_e_invoice', 'SETTINGS.SETTINGS.SAVE_PAPER_ON_E_INVOICE'),
                field.toggle('cashregister.enable_receipt_options', 'SETTINGS.SETTINGS.ENABLE_RECEIPT_OPTIONS'),
                field.toggle('cashregister.public_receipt', 'SETTINGS.SETTINGS.PUBLIC_RECEIPT'),
                {
                    id: 'cashregister.public_receipt_duration',
                    preferenceKey: 'cashregister.public_receipt_duration',
                    control: 'select',
                    numeric: true,
                    label: 'SETTINGS.SETTINGS.PUBLIC_RECEIPT_DURATION.LABEL',
                    options: publicReceiptDurationOptions,
                    disabledWhen: ({ values }) => !values['cashregister.public_receipt']
                }
            ]
        },
        {
            id: 'bookings',
            settings: [
                field.select('bookings.slot_duration', 'SETTINGS.SETTINGS.SLOT_DURATION', { numeric: true, options: () => rangeOptions(15, 240, 15) }),
                field.select('tables.booking_warning', 'SETTINGS.SETTINGS.TABLES_BOOKING_WARNING', { numeric: true, options: bookingWarningOptions }),
                field.toggle('disable_pos_booking_emails', 'SETTINGS.SETTINGS.DISABLE_POS_BOOKING_EMAILS'),
                field.toggle('disable_external_booking_emails', 'SETTINGS.SETTINGS.DISABLE_EXTERNAL_BOOKING_EMAILS')
            ]
        }
    ]
};
